import * as _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import TextareaCell from "./TextareaCell";
import { useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import {
  PresetDataType,
  PresetKeyType,
  TASK_DOMAIN,
  TASK_PRESET_DETAIL_ENDPOINT,
  taskApi,
} from "api";
import { useQuery } from "@tanstack/react-query";
import SelectCell from "./SelectCell";
import { CircularProgress, Stack } from "@mui/joy";

export default function PresetCell({
  taskKey,
  placeholder,
  initialHtmlString,
  initialValue,
  initialSelectedIndex,
  isCoachField,
  isCoach,
  blockIndex,
  readOnly,
  setData,
  options,
  lineIndex,
  cellIndex,
  editorKey,
  presetKey,
}: {
  taskKey: string;
  placeholder?: string;
  initialHtmlString?: string;
  initialValue?: string;
  initialSelectedIndex?: number;
  isCoachField?: boolean;
  isCoach?: boolean;
  blockIndex: number;
  readOnly?: boolean;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  options: string[];
  lineIndex: number;
  cellIndex: number;
  editorKey?: string;
  presetKey?: PresetKeyType;
}) {
  const patientState = useRecoilValue(patientAtom);

  const queryFn = useCallback(
    () => taskApi.getPresetDetail(`${taskKey}`),
    [taskKey]
  );

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(!!initialValue);
    }
  }, [initialValue]);

  const { isFetching } = useQuery(
    [TASK_DOMAIN, TASK_PRESET_DETAIL_ENDPOINT, taskKey],
    queryFn,
    {
      enabled: !!patientState?.patientId && !initialValue && !isLoaded,
      onSuccess: (fetchData) => {
        if (fetchData && fetchData.result) {
          const data = fetchData.result as PresetDataType;
          let value = data[presetKey as PresetKeyType];
          if (value && presetKey === "statementSubject") {
            value = statementSubjectMapping[value as "나" | "타인" | "세상"];
          }

          if (value && !initialValue) {
            setData((data) => {
              if (data) {
                const data_temp = _.cloneDeep(data);

                data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
                  ...data_temp[blockIndex].lines[lineIndex][cellIndex].content,
                  htmlString: value ? `<p>${value}</p>` : undefined,
                  value: value,
                };
                return data_temp;
              }
              return data;
            });
          }
          setIsLoaded(true);
        }
      },
    }
  );

  const statementSubjectMapping = {
    나: "나 자신",
    타인: "나의 인간관계",
    세상: "내가 사는 세상",
  };

  if (isFetching || !isLoaded) {
    return <CircularProgress size={"sm"} color="neutral" />;
  }

  return presetKey === "statementSubject" ? (
    <Stack spacing={0.5} alignItems="end">
      <SelectCell
        defaultValue={initialValue}
        selectedIndex={options?.findIndex(
          (element) => element === initialValue
        )}
        blockIndex={blockIndex}
        options={options}
        setData={setData}
        lineIndex={lineIndex}
        cellIndex={cellIndex}
        isCoachField
        isCoach={isCoach}
      />
    </Stack>
  ) : (
    <Stack spacing={0.5} alignItems="end">
      <TextareaCell
        placeholder={placeholder}
        initialHtmlString={initialHtmlString}
        initialValue={initialValue}
        isCoachField
        isCoach={isCoach}
        setData={setData}
        lineIndex={lineIndex}
        cellIndex={cellIndex}
        blockIndex={blockIndex}
        editorKey={editorKey}
      />
    </Stack>
  );
}